import { memo, useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { MonthlySessionHistoryDto } from '@repo/shared/dtos/analytics/UtilizationDto';
import { ChartWrapper } from '../../shared/ChartWrapper';
import { DarkTooltip } from '../../shared/Tooltip';
import { CHART_THEMES } from '../../config';
import { useResponsiveChartConfig } from '../../shared/useResponsiveChartConfig';
import { palette } from '../../shared/theme';
import { BarChart2 } from 'lucide-react';

interface UserSessionTrendChartProps {
  data: MonthlySessionHistoryDto[];
  onBarClick: (monthYear: string) => void;
  selectedMonth: string | null;
}

/** Converts a "YYYY-MM" string to a short display label like "Mar 2026". */
function formatMonthYear(monthYear: string): string {
  const [year, month] = monthYear.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

export const UserSessionTrendChart = memo(function UserSessionTrendChart({
  data,
  onBarClick,
  selectedMonth,
}: UserSessionTrendChartProps) {
  const { isMobile } = useResponsiveChartConfig();
  // API returns DESC; reverse to show oldest → newest left-to-right
  const chronologicalData = useMemo(() => [...data].reverse(), [data]);

  const chartData = useMemo(
    () =>
      chronologicalData.map(entry => ({
        monthYear: entry.month_year,
        label: formatMonthYear(entry.month_year),
        sessions: entry.session_count,
        minutes: Math.round(entry.total_minutes),
      })),
    [chronologicalData],
  );

  if (!chartData.length) {
    return (
      <div className="flex h-48 items-center justify-center text-[var(--anl-text-muted)]">
        <p className="text-sm">No session history available</p>
      </div>
    );
  }

  const handleClick = (datum: any) => {
    onBarClick(datum.data.monthYear);
  };

  return (
    <div className="rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface)]">
      <div className="flex items-center justify-between gap-2 border-b border-[var(--anl-border)] p-4">
        <h3 className={`flex flex-shrink-0 items-center gap-2 font-semibold text-[var(--anl-text)] ${isMobile ? 'text-sm' : 'text-lg'}`}>
          <BarChart2 className={`text-[var(--anl-text-muted)] ${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
          Monthly Session Trend
        </h3>
        {selectedMonth && (
          <span className="hidden text-sm text-[var(--anl-text-muted)] sm:inline">
            Showing breakdown for{' '}
            <span className="font-medium text-[var(--anl-text)]">
              {formatMonthYear(selectedMonth)}
            </span>{' '}
            — click another bar to switch
          </span>
        )}
        {!selectedMonth && (
          <span className="hidden text-sm text-[var(--anl-text-muted)] sm:inline">
            Click a bar to drill down
          </span>
        )}
      </div>

      <ChartWrapper height={240}>
        <ResponsiveBar
          data={chartData}
          keys={['sessions']}
          indexBy="label"
          onClick={handleClick}
          colors={item => (item.data.monthYear === selectedMonth ? palette.yellow : palette.cyan)}
          margin={isMobile ? { top: 15, right: 20, bottom: 60, left: 45 } : { top: 16, right: 24, bottom: 64, left: 56 }}
          padding={0.3}
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 8,
            tickRotation: -40,
            legend: '',
            legendPosition: 'middle',
            legendOffset: 40,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 8,
            tickRotation: 0,
            legend: 'Sessions',
            legendPosition: 'middle',
            legendOffset: -44,
          }}
          enableLabel={false}
          animate={true}
          theme={CHART_THEMES.default}
          tooltip={({ data: d }: any) => (
            <DarkTooltip>
              <div className="text-center">
                <div className="mb-1 font-semibold">{d.label}</div>
                <div className="text-sm text-[var(--anl-text-muted)]">
                  Sessions:{' '}
                  <span className="font-semibold text-[var(--anl-text)]">{d.sessions}</span>
                </div>
                <div className="text-sm text-[var(--anl-text-muted)]">
                  Minutes: <span className="font-semibold text-[var(--anl-text)]">{d.minutes}</span>
                </div>
                <div className="mt-1 text-xs italic text-[var(--anl-text-muted)]">
                  Click to see experience breakdown
                </div>
              </div>
            </DarkTooltip>
          )}
        />
      </ChartWrapper>
    </div>
  );
});
